import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { readSelectedPlanId } from "@/lib/selected-plan";
import { useEffect, useState } from "react";
import { trackFunnel } from "@/lib/funnel";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import {
  getGuestCheckoutStatus,
  completeGuestSignup,
} from "@/lib/guest-checkout.functions";

type Search = {
  session_id?: string;
  paypal_subscription_id?: string;
  subscription_id?: string;
};

export const Route = createFileRoute("/checkout/complete")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
    paypal_subscription_id:
      typeof search.paypal_subscription_id === "string" && search.paypal_subscription_id
        ? search.paypal_subscription_id
        : undefined,
    subscription_id:
      typeof search.subscription_id === "string" ? search.subscription_id : undefined,
  }),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Create your password — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your Recovery Trauma™ membership payment is confirmed. Choose a password to finish setting up your account.",
      },
      { property: "og:title", content: "Create your password — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Finish setting up your Recovery Trauma™ membership account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CompleteRegistrationPage,
});

function CompleteRegistrationPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const statusFn = useServerFn(getGuestCheckoutStatus);
  const completeFn = useServerFn(completeGuestSignup);

  const paypalId = search.paypal_subscription_id ?? search.subscription_id;
  const payload = search.session_id
    ? { session_id: search.session_id }
    : paypalId
      ? { paypal_subscription_id: paypalId }
      : null;


  const [phase, setPhase] = useState<
    "checking" | "form" | "existing" | "verify" | "done" | "error"
  >("checking");
  const [message, setMessage] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Poll provider truth — the provider, not the client, decides "paid".
  useEffect(() => {
    if (!payload) {
      setPhase("error");
      setMessage("We couldn't find that checkout. Please start again from the membership page.");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    async function check() {
      attempts += 1;
      try {
        const res = await statusFn({ data: payload! });
        if (cancelled) return;
        if (res.ok) {
          // Funnel step 5 — provider-confirmed payment, de-duped per checkout.
          trackFunnel(
            "payment_completed",
            {
              source: "checkout_complete",
              provider: paypalId ? "paypal" : "stripe",
              plan: readSelectedPlanId() ?? undefined,
              dedupeKey: search.session_id ?? paypalId ?? "complete_no_ref",
            },
            { needs_password: res.needs_password },
          );
          setEmail(res.email);
          setPhase(res.needs_password ? "form" : "existing");
          return;
        }
        if (res.reason === "unpaid" && attempts < 8) {
          setTimeout(check, 2000);
          return;
        }
        setPhase("error");
        setMessage(res.message);
      } catch {
        if (attempts < 8) {
          setTimeout(check, 2000);
          return;
        }
        if (!cancelled) {
          setPhase("error");
          setMessage("We couldn't confirm your payment just now. Please refresh in a moment.");
        }
      }
    }

    void check();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    if (password !== confirm) {
      setMessage("Both passwords need to match.");
      return;
    }
    if (!payload) return;
    setSubmitting(true);
    try {
      const res = await completeFn({
        data: { ...payload!, password, origin: window.location.origin },
      });
      if (!res.ok) {
        setMessage(res.message);
        setSubmitting(false);
        return;
      }
      if (res.needs_email_verification) {
        // Payment alone doesn't prove the address belongs to the payer, so the
        // account stays locked until the confirmation link is clicked.
        trackFunnel("account_created", {
          source: "checkout_complete",
          provider: paypalId ? "paypal" : "stripe",
          dedupeKey: `acct:${search.session_id ?? paypalId ?? "complete_no_ref"}`,
        });
        setEmail(res.email);
        setPhase("verify");
        setSubmitting(false);
        return;
      }
      // Sign in straight away so there's no extra step and no dashboard flash.
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.auth.signInWithPassword({
        email: res.email,
        password,
      });
      trackFunnel("account_created", {
        source: "checkout_complete",
        provider: paypalId ? "paypal" : "stripe",
        dedupeKey: `acct:${search.session_id ?? paypalId ?? "complete_no_ref"}`,
      });
      trackFunnel("membership_activated", {
        source: "checkout_complete",
        provider: paypalId ? "paypal" : "stripe",
        dedupeKey: `act:${search.session_id ?? paypalId ?? "complete_no_ref"}`,
      });
      setPhase("done");
      if (error) {
        setMessage("Your account is ready — please sign in with your new password.");
        setTimeout(() => navigate({ to: "/login" }), 1200);
      } else {
        navigate({ to: "/youre-in", replace: true });
      }
    } catch {
      setMessage("Something went wrong finishing your account. Please try again.");
      setSubmitting(false);
    }
  }


  return (
    <main className="mx-auto w-full max-w-lg px-5 py-12">
      {phase === "checking" && (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Confirming your payment with the provider…</p>
        </div>
      )}

      {phase === "error" && (
        <div className="space-y-4 text-center">
          <h1 className="text-2xl font-semibold">We couldn't finish just yet</h1>
          <p className="text-muted-foreground">{message}</p>
          <div className="flex justify-center gap-3">
            <Button asChild variant="outline">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button asChild>
              <Link to="/membership">Membership</Link>
            </Button>
          </div>
        </div>
      )}

      {phase === "existing" && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">Payment successful</h1>
          </div>
          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <p className="text-muted-foreground">
              Thank you — your payment has been confirmed{email ? ` for ${email}` : ""}. You already
              have a Recovery Trauma™ account, and{" "}
              your membership on that account is now active.
            </p>
            <p className="mt-3 text-muted-foreground">
              Sign in with your existing password to open your Recovery Space. Nothing else is
              needed — there's no new account to create.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="sm:flex-1">
                <Link to="/login">Sign in to Recovery Trauma™</Link>
              </Button>
              <Button asChild variant="outline" className="sm:flex-1">
                <Link to="/forgot-password">I've forgotten my password</Link>
              </Button>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            A receipt and invoice are on their way to your inbox.
          </p>
        </div>
      )}

      {phase === "verify" && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">One last step</h1>
          </div>
          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <p className="text-muted-foreground">
              Your payment is confirmed and your account is ready. We've sent a confirmation link to{" "}
              <strong>{email}</strong>. Please open that email and tap the link — it's how we make
              sure this address really belongs to you, so nobody else can use it to reach your
              private space.
            </p>
            <p className="mt-3 text-muted-foreground">
              Once you've confirmed, sign in with the password you just chose.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="sm:flex-1">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Nothing in your inbox after a few minutes? Check your spam folder, or contact support and
            we'll help.
          </p>
        </div>
      )}

      {(phase === "form" || phase === "done") && (
        <>
          <div className="mb-6 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">Payment confirmed</h1>
          </div>
          <p className="text-muted-foreground">
            Welcome to Recovery Trauma™{email ? ` — ${email}` : ""}.{" "}
            Your membership is active. Choose a password to finish setting up your account and open your Recovery Space.
          </p>


          <form onSubmit={onSubmit} className="mt-6 space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
            <div className="space-y-2">
              <Label htmlFor="new-password">Create a password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                At least 8 characters, with a letter and a number.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>

            {message && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {message}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
              Create my account
            </Button>
          </form>
        </>
      )}
    </main>
  );
}
